import {
    HorizontalStack,
    componentStyles,
    useColors,
} from "@mallardbay/lib-react-components"
import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import type { ReactionIconMapItem } from "~components/shared/activity-card/activity-reactions"
import { TEST_IDS } from "~config/test-ids"
import type {
    ActivityFieldsFragment,
    ReactionFieldsFragment,
    ReactionType,
} from "~graphql/generated/graphql"
import { useAuth } from "~utils/auth-hooks"
import { useReactToActivity, useRemoveReaction } from "~utils/stream-feeds"
import { TextSecondary } from "~components/shared/todo-lib-react-components/text"

interface Props {
    readonly iconItem: ReactionIconMapItem
    readonly activity: ActivityFieldsFragment
}

const ICON_SIZE = 20

export default function ActivityReaction({ iconItem, activity }: Props) {
    const styles = useStyles()
    const { reactedIcon, notReactedIcon, type } = iconItem
    const ownReaction = activity.own_reactions?.[type]?.[0]
    const reactionCount = activity.reaction_counts?.[type]

    const onIconClick = useGetIconOnClick({
        type,
        ownReaction,
        activity,
    })

    const onClick: React.MouseEventHandler<SVGElement> = (event) => {
        //prevent opening detail view if icon is clicked
        event.stopPropagation()

        return onIconClick()
    }

    const reactionCountText = reactionCount || ""
    const hasReacted = !!ownReaction
    const icon = hasReacted ? reactedIcon : notReactedIcon
    const style = hasReacted ? styles.reactedIcon : styles.notReactedIcon
    const testId = hasReacted
        ? TEST_IDS.REACTED_ICON
        : TEST_IDS.NOT_REACTED_ICON

    return (
        <HorizontalStack style={styles.root}>
            <FontAwesomeIcon
                style={style}
                icon={icon}
                onClick={onClick}
                data-testid={testId}
            />
            <TextSecondary>{reactionCountText}</TextSecondary>
        </HorizontalStack>
    )
}

function useGetIconOnClick({
    type,
    ownReaction,
    activity,
}: {
    type: ReactionType
    ownReaction?: ReactionFieldsFragment
    activity: ActivityFieldsFragment
}) {
    const hasReacted = !!ownReaction
    const { loginWithRedirect, isAuthenticated } = useAuth()
    const { reactToActivity } = useReactToActivity({
        activity,
        reactionType: type,
    })

    const { removeReaction } = useRemoveReaction({
        activity,
        // for removeReaction to be visible, ownReaction must be defined
        reaction: ownReaction,
        reactionType: type,
    })

    if (!isAuthenticated) {
        return loginWithRedirect
    }

    if (hasReacted) {
        return removeReaction
    }

    return reactToActivity
}

function useStyles() {
    const colors = useColors()

    return componentStyles({
        root: {
            gap: "8px",
        },
        reactedIcon: {
            color: colors.error,
            fontSize: ICON_SIZE,
        },
        notReactedIcon: {
            fontSize: ICON_SIZE,
        },
    })
}
